import type { Component } from '@acies/core';
import { Container, InteractiveContainerEvent } from '@acies/core';
import ToggleButton from './toggle-button';
import ToggleButtonBarEvent from './toggle-button-bar-event';
import ToggleButtonEvent from './toggle-button-event';

/**
 * The TogglebuttonBar component wraps ToggleButton components and is responsible for setting the selected property of these child components.
 *
 * When a child ToggleButton is clicked and selected, the ToggleButtonBar de-selects the other children and dispatches a ToggleButtonBarEvent, that has the selectedIndex property set to the index of the selected ToggleButton child.
 *
 * The cornerRadius of the children are automatically set by the ToggleButtonBar component, so we get square corners if the children has neighbors to the right and / or left.
 */
export default class ToggleButtonBar extends Container {
	public static override observedAttributes = [...Container.observedAttributes, 'selected-index'];
	/**
	 * The selectedIndex property is set when a ToggleButton child is selected by the user.
	 *
	 * If the selectedIndex is set on the TogglebuttonBar component, the child at that selectedIndex will get it's selected property set to true and the other children selected property set to false.
	 */
	public set selectedIndex(value: number) {
		assertIsNonNegativeInteger(value);
		this.#selectedIndex = value;
		this.#selectedIndexChanged = true;
		this.invalidateProperties();
	}

	public get selectedIndex(): number {
		return this.#selectedIndex;
	}

	#selectedIndex: number;
	#selectedIndexChanged: boolean;

	public constructor() {
		super();
		this.#selectedIndex = 0;
		this.#selectedIndexChanged = false;
	}

	override addComponent(value: Component): void {
		super.addComponent(value);
		this.#assertAndUpdateChildren();
		this.#updateSelectedChildFromSelectedIndex();
		this.#setSelectedChildZIndexToTopLevel();
	}

	override addComponents(value: Component[]): void {
		super.addComponents(value);
		this.#assertAndUpdateChildren();
		this.#updateSelectedChildFromSelectedIndex();
		this.#setSelectedChildZIndexToTopLevel();
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'selected-index') {
			this.#selectedIndexAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#selectedIndexChanged) {
			this.#commitSelectedIndex();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.#assertAndUpdateChildren();
		this.#updateSelectedChildFromSelectedIndex();
		this.#setSelectedChildZIndexToTopLevel();
		this.addEventListener(ToggleButtonEvent.SELECTED_CHANGED, this.#childSelectedPropertyChanged as EventListener);
		this.addEventListener(InteractiveContainerEvent.BLURRED, this.#handleInteractiveContainerEvents);
		this.addEventListener(InteractiveContainerEvent.FOCUSED, this.#handleInteractiveContainerEvents);
		this.addEventListener(InteractiveContainerEvent.HOVERED, this.#handleInteractiveContainerEvents);
		this.addEventListener(InteractiveContainerEvent.UN_HOVERED, this.#handleInteractiveContainerEvents);
	}

	#assertAndUpdateChildren(): void {
		assertChildrenAreInstanceOfToggleButton(this.children);
		this.#updateChildrenCornerRadii();
	}

	#childSelectedPropertyChanged(event: ToggleButtonEvent): void {
		event.stopPropagation();
		this.#updateChildrenSelectedProperties(event);
		this.#updatedSelectedIndexFromSelectedChild();
		this.#setSelectedChildZIndexToTopLevel();
		this.#dispatchToggleButtonBarEvent();
	}

	#commitSelectedIndex(): void {
		this.#selectedIndexChanged = false;
		this.#updateSelectedChildFromSelectedIndex();
		this.#setSelectedChildZIndexToTopLevel();
	}

	#deSelectAllChildren(): void {
		for (const child of this.children) {
			assertIsToggleButton(child);
			child.selected = false;
		}
	}

	#dispatchToggleButtonBarEvent(): void {
		this.dispatchEvent(new ToggleButtonBarEvent(this.selectedIndex));
	}

	/** Stops every one of them, but only the focus moves a child on top: hovering is not styled, so there is no stroke under the pointer to lift above the neighbours. */
	#handleInteractiveContainerEvents(event: InteractiveContainerEvent): void {
		event.stopPropagation();
		if (event.type === InteractiveContainerEvent.HOVERED || event.type === InteractiveContainerEvent.UN_HOVERED) {
			return;
		}
		assertIsToggleButton(event.target);
		this.#placeToggleButtonChildAboveOtherChildren(event.target);
	}

	#placeToggleButtonChildAboveOtherChildren(value: ToggleButton): void {
		for (const child of this.children) {
			assertIsToggleButton(child);
			// if the child is the current selected one we set the zIndex to top level
			if (child === value || child.selected) {
				child.zIndex = 1;
			} else {
				child.zIndex = 'auto';
			}
		}
	}

	#selectedIndexAttributeChanged(value: string): void {
		this.selectedIndex = Number(value);
	}

	#setAllChildrenCornerRadius(): void {
		for (const child of this.children) {
			if (child instanceof ToggleButton) {
				this.#setChildCornerRadius(child);
			}
		}
	}

	#setChildCornerRadius(child: ToggleButton): void {
		let topLeft = 0;
		let topRight = 0;
		let bottomRight = 0;
		let bottomLeft = 0;
		if (child.previousElementSibling === null) {
			topLeft = 8;
			bottomLeft = 8;
		}
		if (child.nextElementSibling === null) {
			topRight = 8;
			bottomRight = 8;
		}
		child.cornerRadius = [topLeft, topRight, bottomRight, bottomLeft];
	}

	#setSelectedChildZIndexToTopLevel(): void {
		for (const child of this.children) {
			assertIsToggleButton(child);
			if (child.selected) {
				child.zIndex = 1;
			}
		}
	}

	#setSelectedPropertyFromEvent(child: ToggleButton, event: ToggleButtonEvent): void {
		if (event.selected) {
			if (child !== event.target) {
				child.selected = false;
			}
		}
	}

	#setSingleChildCornerRadius(): void {
		const child = this.children.item(0);
		if (child instanceof ToggleButton) {
			child.cornerRadius = 8;
		}
	}

	#updateChildrenCornerRadii(): void {
		if (this.children.length === 1) {
			this.#setSingleChildCornerRadius();
		} else {
			this.#setAllChildrenCornerRadius();
		}
	}

	#updateChildrenSelectedProperties(event: ToggleButtonEvent): void {
		for (const child of this.children) {
			if (child instanceof ToggleButton) {
				this.#setSelectedPropertyFromEvent(child, event);
			}
		}
	}

	#updatedSelectedIndexFromSelectedChild(): void {
		let selectedIndex = 0;
		for (let index = 0; index < this.children.length; index++) {
			const child = this.children.item(index);
			if (child instanceof ToggleButton && child.selected) {
				selectedIndex = index;
			}
		}
		this.#selectedIndex = selectedIndex;
	}

	#updateSelectedChildFromSelectedIndex(): void {
		if (this.children.length > 0) {
			this.#deSelectAllChildren();
			assertSelectedIndexIsSelectable(this.selectedIndex, this.children);
			const child = this.children.item(this.selectedIndex);
			assertIsToggleButton(child);
			child.selected = true;
		}
	}
}
customElements.define('ac-toggle-button-bar', ToggleButtonBar);

function assertChildrenAreInstanceOfToggleButton(children: HTMLCollection): asserts children is HTMLCollection {
	for (const child of children) {
		if (child instanceof ToggleButton === false) {
			throw new TypeError(`[${String(child)}] is not valid, all ToggleButtonBar children must be instances of the ToggleButton class or the "<ac-toggle-button>" html tag`);
		}
	}
}

function assertIsNonNegativeInteger(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0 && Number.isInteger(value)) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be a non negative integer`);
}

function assertIsToggleButton(value: unknown): asserts value is ToggleButton {
	if (value instanceof ToggleButton) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be instance of ToggleButton`);
}

function assertSelectedIndexIsSelectable(selectedIndex: number, children: HTMLCollection): void {
	if (children.item(selectedIndex) instanceof ToggleButton) {
		return;
	}
	throw new TypeError(`[${String(selectedIndex)}] is invalid, no ToggleButton is located at this selectedIndex`);
}
